import React from 'react';
import { DimensionsIcon, StackIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text } from '@/shared/ui';
import type { PropertyResponseType } from '@services';
import { PropertyNotesSection } from '../PropertyNotesSection';
import { OverviewExtraSections } from './OverviewExtraSections';
import { PropertyVideosSection } from './PropertyVideosSection';
import { SectionCard, EditCardButton, EmptyCardHint } from '../SectionCard';
import type { PropertyDrawerSection } from '../../PropertyDrawer/drawerSections';

interface OverviewTabProps {
  property: PropertyResponseType;
  agencyId?: string;
  propertyId?: string;
  /** Opens the property editor on one section, from a card's pencil. */
  onEditSection?: (section: PropertyDrawerSection) => void;
}

interface StatItem {
  icon: React.ReactNode;
  label: string;
  value: string;
}

interface StatDescriptor {
  key: string;
  icon: React.ReactNode;
  resolve: (property: PropertyResponseType) => number | bigint | undefined;
  format: (value: number) => string;
}

const dimensionIcon = <DimensionsIcon width={16} height={16} />;
const stackIcon = <StackIcon width={16} height={16} />;
/**
 * Formats a numeric value as a square-meter area string.
 * @returns The formatted area string.
 */
const asMeters = (value: number): string => `${value} m²`;

const STAT_DESCRIPTORS: StatDescriptor[] = [
  { key: 'area', icon: dimensionIcon, resolve: (p) => p.characteristics?.total_area ?? undefined, format: asMeters },
  { key: 'bedrooms', icon: stackIcon, resolve: (p) => p.characteristics?.bedrooms ?? undefined, format: String },
  { key: 'suites', icon: stackIcon, resolve: (p) => p.characteristics?.suites ?? undefined, format: String },
  { key: 'bathrooms', icon: stackIcon, resolve: (p) => p.characteristics?.bathrooms ?? undefined, format: String },
  { key: 'parking', icon: stackIcon, resolve: (p) => p.characteristics?.parking_spots ?? undefined, format: String },
  { key: 'constructedArea', icon: dimensionIcon, resolve: (p) => p.characteristics?.constructed_area ?? undefined, format: asMeters },
  { key: 'floor', icon: stackIcon, resolve: (p) => p.characteristics?.floor_number ?? undefined, format: String },
  { key: 'yearBuilt', icon: stackIcon, resolve: (p) => p.characteristics?.year_built ?? undefined, format: String },
];

/**
 * Builds the list of key-characteristic stat items shown for a property,
 * skipping any characteristic whose value is absent or zero.
 */
function buildStats(property: PropertyResponseType, t: TFunction): StatItem[] {
  return STAT_DESCRIPTORS.flatMap((descriptor) => {
    const value = descriptor.resolve(property);
    if (!value) return [];
    return [{
      icon: descriptor.icon,
      label: t(`detailView.overview.stats.${descriptor.key}`),
      value: descriptor.format(Number(value)),
    }];
  });
}

/**
 * Renders the overview tab: description, key stats, tags, commercial terms,
 * media links and notes.
 *
 * The gallery used to open this tab. It now sits beside the header, where the
 * photos belong to the listing rather than to one of its six tabs — a broker
 * checking the visits tab should not lose sight of what the place looks like.
 *
 * @param props.property - The loaded property.
 * @param props.agencyId - Agency owning the property; enables notes.
 * @param props.propertyId - Property the notes belong to.
 * @param props.onEditSection - Opens the editor; the description card stays
 * visible while empty so it can be filled in from its pencil.
 * @returns The rendered tab.
 */
export const OverviewTab: React.FC<OverviewTabProps> = ({ property, agencyId, propertyId, onEditSection }) => {
  const { t } = useTranslation('features/properties');
  const stats = buildStats(property, t);

  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      <SectionCard
        title={t('detailView.overview.description')}
        data-testid="property-description-card"
        titleAccessory={
          onEditSection ? (
            <EditCardButton onClick={() => onEditSection('description')} data-testid="property-description-edit" />
          ) : undefined
        }
      >
        {property.description?.full_text ? (
          <Text className="m-0 whitespace-pre-line text-[length:var(--text-body-sm)] leading-[var(--leading-relaxed)] text-[var(--text-primary)]">
            {property.description.full_text}
          </Text>
        ) : (
          <EmptyCardHint />
        )}
      </SectionCard>

      {stats.length > 0 && (
        <SectionCard title={t('detailView.overview.mainCharacteristics')}>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,150px),1fr))] gap-[var(--space-3)]">
            {stats.map((item) => (
              <div
                key={item.label}
                className="flex min-w-0 items-center gap-[var(--space-2)] rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-2)]"
              >
                <Text component="span" className="flex flex-none text-[var(--seed-base)]">{item.icon}</Text>
                <div className="flex min-w-0 flex-col">
                  <Text
                    component="span"
                    className="truncate text-[length:var(--text-meta)] leading-[var(--leading-snug)] text-[var(--text-tertiary)]"
                  >
                    {item.label}
                  </Text>
                  <Text
                    component="span"
                    className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-[var(--text-primary)]"
                  >
                    {item.value}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <PropertyVideosSection
        agencyId={agencyId}
        propertyId={propertyId}
        links={property.media?.videos}
        refreshKey={property}
        onEditSection={onEditSection}
      />

      <OverviewExtraSections property={property} />

      {agencyId && propertyId && (
        <PropertyNotesSection agencyId={agencyId} propertyId={propertyId} />
      )}
    </div>
  );
};
